/**
 * Набор иконок интерфейса.
 *
 * Все иконки — контурные SVG 16×16 на currentColor: они наследуют цвет кнопки
 * и одинаково выглядят в активном, обычном и выключенном состоянии.
 * Текстовые глифы для этого не годятся — они разного размера в разных шрифтах.
 */
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
			{...props}
		>
			{children}
		</svg>
	);
}

export const IconCursor = (p: IconProps) => (
	<Icon {...p}>
		<path d="M3 2.5l9.5 5.2-4.2 1.1-1.6 4.2z" />
	</Icon>
);

/** Комментарий: облачко-выноска с двумя строками текста */
export const IconComment = (p: IconProps) => (
	<Icon {...p}>
		<path d="M2.5 3h11v7.5H7l-3 2.5v-2.5H2.5z" />
		<path d="M5 5.8h6M5 8h4" />
	</Icon>
);

export const IconWall = (p: IconProps) => (
	<Icon {...p}>
		<rect x="1.5" y="5.5" width="13" height="5" rx="0.5" />
		<path d="M6 5.5v5M10.5 5.5v5" />
	</Icon>
);

export const IconDoor = (p: IconProps) => (
	<Icon {...p}>
		<path d="M3 13V3.5h6.5V13" />
		<path d="M1.5 13h13" />
		<circle cx="7.8" cy="8.4" r="0.7" fill="currentColor" stroke="none" />
	</Icon>
);

export const IconWindow = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2.5" y="3" width="11" height="10" rx="0.5" />
		<path d="M8 3v10M2.5 8h11" />
	</Icon>
);

export const IconUndo = (p: IconProps) => (
	<Icon {...p}>
		<path d="M3 6h6.5a3.5 3.5 0 010 7H6" />
		<path d="M5.5 3L2.5 6l3 3" />
	</Icon>
);

export const IconRedo = (p: IconProps) => (
	<Icon {...p}>
		<path d="M13 6H6.5a3.5 3.5 0 000 7H10" />
		<path d="M10.5 3l3 3-3 3" />
	</Icon>
);

export const IconDimensions = (p: IconProps) => (
	<Icon {...p}>
		<path d="M2 8h12" />
		<path d="M4.5 5.5L2 8l2.5 2.5M11.5 5.5L14 8l-2.5 2.5" />
		<path d="M2 3.5v2M14 3.5v2" />
	</Icon>
);

export const IconArea = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2.5" y="2.5" width="11" height="11" rx="0.5" strokeDasharray="2.4 1.8" />
		<path d="M5.5 10.5l5-5" />
	</Icon>
);

/** Способ черчения «Цепочка»: ломаная из стен */
export const IconWallChain = (p: IconProps) => (
	<Icon {...p}>
		<path d="M2.5 12.5v-6h5v4h6v-7" />
		<circle cx="2.5" cy="12.5" r="1" fill="currentColor" />
		<circle cx="13.5" cy="3.5" r="1" fill="currentColor" />
	</Icon>
);

/** Способ черчения «Одна стена»: отрезок с двумя точками */
export const IconWallSingle = (p: IconProps) => (
	<Icon {...p}>
		<path d="M3 12.5l10-9" />
		<circle cx="3" cy="12.5" r="1" fill="currentColor" />
		<circle cx="13" cy="3.5" r="1" fill="currentColor" />
	</Icon>
);

/** Способ черчения «Прямоугольник»: комната по двум углам диагонали */
export const IconWallRect = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2.5" y="3.5" width="11" height="9" />
		<path d="M2.5 12.5l11-9" strokeDasharray="1.6 1.6" />
		<circle cx="2.5" cy="12.5" r="1" fill="currentColor" />
		<circle cx="13.5" cy="3.5" r="1" fill="currentColor" />
	</Icon>
);

/** Способ черчения «Повёрнутый прямоугольник»: первая стена под углом, потом ширина */
export const IconWallRotated = (p: IconProps) => (
	<Icon {...p}>
		<path d="M2 8.5l5.5-5.5 6.5 6.5-5.5 5.5z" />
		<circle cx="2" cy="8.5" r="1" fill="currentColor" />
		<circle cx="7.5" cy="3" r="1" fill="currentColor" />
		<circle cx="14" cy="9.5" r="1" fill="currentColor" />
	</Icon>
);

export const IconSave = (p: IconProps) => (
	<Icon {...p}>
		<path d="M2.5 2.5h8.5L13.5 5v8.5h-11z" />
		<path d="M5 2.5v4h6M5 13.5v-3.5h6v3.5" />
	</Icon>
);

export const IconDownload = (p: IconProps) => (
	<Icon {...p}>
		<path d="M8 2v8" />
		<path d="M5 7l3 3 3-3" />
		<path d="M2.5 12.5v1.5h11v-1.5" />
	</Icon>
);

export const IconPrint = (p: IconProps) => (
	<Icon {...p}>
		<path d="M4 5V2.5h8V5" />
		<rect x="3" y="9" width="10" height="4.5" />
		<path d="M2 6h12v5H2zM11.5 8.2h.01" />
	</Icon>
);

/** Калькулятор — смета. */
export const IconEstimate = (p: IconProps) => (
	<Icon {...p}>
		<rect x="3" y="1.5" width="10" height="13" rx="1.5" />
		<path d="M5.5 4.5h5M5.5 8h.01M8 8h.01M10.5 8h.01M5.5 10.5h.01M8 10.5h.01M10.5 10.5h.01M5.5 13h.01M8 13h.01M10.5 13h.01" />
	</Icon>
);

export const IconCheck = (p: IconProps) => (
	<Icon {...p}>
		<path d="M3 8.5l3.5 3.5L13 4.5" />
	</Icon>
);

export const IconDemo = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2.5" y="2.5" width="11" height="11" rx="1" />
		<path d="M2.5 6.5h11M6.5 6.5v7" />
	</Icon>
);

export const IconTrash = (p: IconProps) => (
	<Icon {...p}>
		<path d="M2.5 4h11M6 4V2.5h4V4" />
		<path d="M4 4l.7 9.5h6.6L12 4" />
	</Icon>
);

export const IconView2D = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2" y="2.5" width="12" height="11" rx="0.5" />
		<path d="M2 6.5h12M7 6.5v7" />
	</Icon>
);

export const IconView3D = (p: IconProps) => (
	<Icon {...p}>
		<path d="M8 1.8l5.5 3v6.4L8 14.2 2.5 11.2V4.8z" />
		<path d="M2.5 4.8L8 7.9l5.5-3.1M8 7.9v6.3" />
	</Icon>
);

export const IconCutaway = (p: IconProps) => (
	<Icon {...p}>
		<path d="M2.5 13V6l5.5-3.2L13.5 6v7" />
		<path d="M1.5 13h13" />
		<path d="M8 13V8.5h3.2V13" strokeDasharray="2 1.6" />
	</Icon>
);

export const IconCeiling = (p: IconProps) => (
	<Icon {...p}>
		<path d="M1.5 4.5h13" />
		<path d="M3.5 4.5v8M12.5 4.5v8" />
		<path d="M6 7.5h4" strokeDasharray="1.6 1.4" />
	</Icon>
);

export const IconSun = (p: IconProps) => (
	<Icon {...p}>
		<circle cx="8" cy="8" r="3" />
		<path d="M8 1.5v1.6M8 12.9v1.6M1.5 8h1.6M12.9 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M12.6 3.4l-1.1 1.1M4.5 11.5l-1.1 1.1" />
	</Icon>
);

export const IconMoon = (p: IconProps) => (
	<Icon {...p}>
		<path d="M12.9 10.3A5.3 5.3 0 0 1 5.7 3.1a5.3 5.3 0 1 0 7.2 7.2Z" />
	</Icon>
);

/** Лампочка: колба и цоколь */
export const IconLamp = (p: IconProps) => (
	<Icon {...p}>
		<path d="M5.2 10.2C4 9.2 3.5 8 3.5 6.8a4.5 4.5 0 0 1 9 0c0 1.2-.5 2.4-1.7 3.4v1.3H5.2Z" />
		<path d="M6 13.5h4" />
	</Icon>
);

export const IconGrid = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2" y="2" width="12" height="12" rx="0.5" />
		<path d="M6 2v12M10 2v12M2 6h12M2 10h12" />
	</Icon>
);

/** Схема помещений с границами комнат. */
export const IconRoomMap = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2" y="2.5" width="12" height="11" rx="0.5" />
		<path d="M7 2.5v5h7M2 9h4v4.5M9.5 7.5v6" />
	</Icon>
);

/** Стрелки управления движением камеры. */
export const IconMove = (p: IconProps) => (
	<Icon {...p}>
		<path d="M8 2v12M2 8h12M8 2l-2 2M8 2l2 2M8 14l-2-2M8 14l2-2M2 8l2-2M2 8l2 2M14 8l-2-2M14 8l-2 2" />
	</Icon>
);

export const IconReset = (p: IconProps) => (
	<Icon {...p}>
		<path d="M13.2 8a5.2 5.2 0 1 1-1.6-3.7" />
		<path d="M13.4 2.6v3.1h-3.1" />
	</Icon>
);

export const IconElevation = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2" y="3" width="12" height="8" rx="0.5" />
		<path d="M6 3v8" />
		<path d="M2 13.5h12M2.8 12.7v1.6M13.2 12.7v1.6" />
	</Icon>
);

export const IconSocket = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
		<path d="M5 9.5a3 3 0 0 1 6 0" />
		<path d="M4.6 9.5h6.8" />
		<circle cx="6.6" cy="7.6" r="0.6" fill="currentColor" stroke="none" />
		<circle cx="9.4" cy="7.6" r="0.6" fill="currentColor" stroke="none" />
	</Icon>
);

export const IconHelp = (p: IconProps) => (
	<Icon {...p}>
		<circle cx="8" cy="8" r="6.2" />
		<path d="M6.2 6.3a1.9 1.9 0 0 1 3.7.5c0 1.2-1.9 1.4-1.9 2.6" />
		<circle cx="8" cy="11.6" r="0.55" fill="currentColor" stroke="none" />
	</Icon>
);

/** Действия, не поместившиеся в компактную панель. */
export const IconMore = (p: IconProps) => (
	<Icon {...p}>
		<circle cx="3.5" cy="8" r="0.75" fill="currentColor" stroke="none" />
		<circle cx="8" cy="8" r="0.75" fill="currentColor" stroke="none" />
		<circle cx="12.5" cy="8" r="0.75" fill="currentColor" stroke="none" />
	</Icon>
);

/** Переключатель ширины панели инструментов. */
export const IconToolRail = ({ isOpen, ...props }: IconProps & { isOpen?: boolean }) => (
	<Icon {...props}>
		<rect x="2" y="2" width="12" height="12" rx="1.5" />
		<path d="M6 2v12" />
		{isOpen ? <path d="M13 8H9M11 6l-2 2 2 2" /> : <path d="M8 8h4M11 6l2 2-2 2" />}
	</Icon>
);

export const IconLayers = (p: IconProps) => (
	<Icon {...p}>
		<path d="M8 2.5l6 3-6 3-6-3z" />
		<path d="M2 8.5l6 3 6-3" />
		<path d="M2 11.5l6 3 6-3" />
	</Icon>
);

/** Объекты по слоям: каталог размещаемых элементов сцены. */
export const IconObjectLayers = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2" y="3" width="5" height="4" rx="0.5" />
		<rect x="9" y="3" width="5" height="4" rx="0.5" />
		<rect x="5.5" y="9" width="5" height="4" rx="0.5" />
		<path d="M4.5 7v1.2M11.5 7v1.2M8 8.2V9" />
	</Icon>
);

export const IconProperties = (p: IconProps) => (
	<Icon {...p}>
		<path d="M3 4h10M3 8h10M3 12h10" />
		<circle cx="6" cy="4" r="1" fill="currentColor" stroke="none" />
		<circle cx="10" cy="8" r="1" fill="currentColor" stroke="none" />
		<circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" />
	</Icon>
);

export const IconEye = (p: IconProps) => (
	<Icon {...p}>
		<path d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" />
		<circle cx="8" cy="8" r="2" />
	</Icon>
);

export const IconEyeOff = (p: IconProps) => (
	<Icon {...p}>
		<path d="M2.5 2.5l11 11" />
		<path d="M6.3 6.4A2 2 0 0 0 9.6 9.7" />
		<path d="M4.2 4.4C2.5 5.6 1.5 8 1.5 8s2.5 4.5 6.5 4.5c1.3 0 2.4-.4 3.4-1" />
		<path d="M6.6 3.7C7 3.6 7.5 3.5 8 3.5c4 0 6.5 4.5 6.5 4.5s-.6 1.1-1.7 2.2" />
	</Icon>
);

/** Человек: прогулка по модели от первого лица. */
export const IconPerson = (p: IconProps) => (
	<Icon {...p}>
		<circle cx="8" cy="3.5" r="1.8" fill="currentColor" stroke="none" />
		<path d="M5.5 6.5h5l1 7h-2.2V10h-2.6v3.5H4.5z" fill="currentColor" stroke="none" />
	</Icon>
);

export const IconCable = (p: IconProps) => (
	<Icon {...p}>
		<circle cx="3" cy="12.5" r="1.6" />
		<circle cx="13" cy="3.5" r="1.6" />
		<path d="M4.6 12.5H8.5V3.5h2.9" />
	</Icon>
);

/** Труба водоснабжения: колено с двумя выводами */
export const IconPipe = (p: IconProps) => (
	<Icon {...p}>
		<path d="M2.5 4.5h6.5a2 2 0 0 1 2 2v7" />
		<path d="M2.5 7.5h6a1 1 0 0 1 1 1v5" />
		<path d="M1.5 3v6M13 13.5h-4" />
	</Icon>
);

/** Мебель: диван сбоку */
export const IconFurniture = (p: IconProps) => (
	<Icon {...p}>
		<path d="M3 12V8.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 .5.5V12" />
		<path d="M5 8V5.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 .5.5V8" />
		<path d="M1.5 12h13" />
	</Icon>
);

/** Шкаф из конструктора: корпус с двумя дверями и ручками */
export const IconWardrobe = (p: IconProps) => (
	<Icon {...p}>
		<rect x="3" y="1.5" width="10" height="12" rx="0.5" />
		<path d="M8 1.5v12M6.5 7v1.5M9.5 7v1.5M4 13.5v1M12 13.5v1" />
	</Icon>
);

/** Открытые фасады: корпус с распахнутой дверцей */
export const IconFrontsOpen = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2" y="2" width="8" height="12" rx="0.5" />
		<path d="M10 2l4 1.5v9.5l-4 1M12.4 7.5v1.5" />
	</Icon>
);

/** Модуль: комод с тремя ящиками на ножках */
export const IconChest = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2.5" y="2.5" width="11" height="10" rx="0.5" />
		<path d="M2.5 6h11M2.5 9.3h11M7 4.2h2M7 7.6h2M7 11h2M4 12.5v1.5M12 12.5v1.5" />
	</Icon>
);

/** Кухня: нижние шкафы со столешницей, плита и вытяжка */
export const IconKitchen = (p: IconProps) => (
	<Icon {...p}>
		<path d="M1.5 9h13v5.5h-13zM6 9v5.5M10.5 9v5.5" />
		<path d="M7.5 2h3l1 3.5h-5zM9 2V0.8" />
	</Icon>
);

/** Сантехника: ванна сбоку */
export const IconBath = (p: IconProps) => (
	<Icon {...p}>
		<path d="M1.5 7.5h13" />
		<path d="M3 7.5v4a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-4" />
		<path d="M4 7.5V4.5a.5.5 0 0 1 .5-.5H5" />
	</Icon>
);

/** Декор: картина в рамке */
export const IconDecor = (p: IconProps) => (
	<Icon {...p}>
		<rect x="3" y="3.5" width="10" height="7" rx=".5" />
		<path d="M3 8.5l3-2.5 2.5 2.5 2-1.5 2.5 1.5" />
	</Icon>
);

/** Напольное покрытие: плитка 2×2 */
export const IconFloorTile = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2.5" y="5" width="11" height="8.5" rx=".5" />
		<path d="M8 5v8.5M2.5 9.5h11" />
	</Icon>
);

/** Отделка стен: малярный валик */
export const IconPaintRoller = (p: IconProps) => (
	<Icon {...p}>
		<rect x="2.5" y="2" width="7" height="3" rx=".7" />
		<path d="M9.5 3.5h3v4H8.5" />
		<path d="M8.5 7.5v5.5" />
	</Icon>
);

/** Гидроизоляция: капля воды */
export const IconWaterDrop = (p: IconProps) => (
	<Icon {...p}>
		<path d="M8 1.5c-2 3.5-4.5 5.5-4.5 8a4.5 4.5 0 1 0 9 0c0-2.5-2.5-4.5-4.5-8z" />
	</Icon>
);

/** Стяжка: правило на стяжке */
export const IconScreed = (p: IconProps) => (
	<Icon {...p}>
		<rect x="1.5" y="5.5" width="13" height="3" rx=".5" />
		<path d="M4.5 8.5v4M8 8.5v4M11.5 8.5v4" />
		<path d="M1.5 13h13" />
	</Icon>
);

/** Водоснабжение: кран */
export const IconFaucet = (p: IconProps) => (
	<Icon {...p}>
		<path d="M5 12V8.5a3 3 0 0 1 3-3h3" />
		<path d="M9 4h4.5" />
		<path d="M3 12h4" />
	</Icon>
);
